import { all, put, select } from 'redux-saga/effects';

import { logger } from '@/config/logger';
import { groupsQuery, type GroupsQueryParams } from '@/modules/api/codegen';
import { actions as messagesActions } from '@/modules/messages';
import { takeLatestRequest } from '@/services/utilities/takeRequest';

import { fetchGroups, fetchGroupsCancel, fetchGroupsFailure, fetchGroupsNext, fetchGroupsRequest, fetchGroupsSuccess } from '../actions';
import { normalizeGroups } from '../normalizers';
import { selectGroupsApi } from '../selectors';

function* handler(action: ReturnType<typeof fetchGroups> | ReturnType<typeof fetchGroupsNext>, signal: AbortSignal) {
    const apiState: ReturnType<typeof selectGroupsApi> = yield select(selectGroupsApi);

    const isNext = action.type === fetchGroupsNext.toString();

    if (isNext && !apiState.nextPageToken) {
        return;
    }

    const params: GroupsQueryParams = {};

    if (action.meta.filter) {
        params.query = action.meta.filter;
    }

    if (action.meta.onlyVisible) {
        params.onlyVisible = action.meta.onlyVisible;
    }

    if (isNext) {
        params.nextPageToken = apiState.nextPageToken;
    }

    yield put(fetchGroupsRequest(isNext));

    try {
        const { data } = yield groupsQuery(params, { signal });

        const { entities, result } = normalizeGroups(data.data ?? []);

        yield put(
            fetchGroupsSuccess(entities.groups, result, {
                filter: action.meta.filter,
                nextPageToken: data.metadata.nextPageToken,
                isNext,
            }),
        );
    } catch (e) {
        yield put(
            messagesActions.displayErrorMessage({
                message: { id: 'error.groups.fetch' },
            }),
        );
        logger.error(e);

        yield put(fetchGroupsFailure((e as Error).message, isNext));
    }
}

export function* fetchGroupsSaga(): Generator {
    yield all([
        yield takeLatestRequest(
            {
                actionCreator: fetchGroups,
                cancelActionFunction: () => fetchGroupsCancel(),
                idSelector: () => 'groups',
            },
            handler,
        ),
        yield takeLatestRequest(
            {
                actionCreator: fetchGroupsNext,
                cancelActionFunction: () => fetchGroupsCancel(true),
                idSelector: () => 'groups',
            },
            handler,
        ),
    ]);
}
